<!-- ProseMessage: full-width prose-style chat message. -->
<template>
  <li
    ref="messageEl"
    class="prose-message chat-editable-entry"
    :data-chat-editing="isEditing"
    :class="{
      'prose-message--user': isUserAuthor,
      'prose-message--ai': !isUserAuthor,
      'prose-message--editing': isEditing,
      [`status-${props.message.phase}`]: props.message.phase !== 'complete',
    }"
  >
    <!-- Main body -->
    <div
      class="prose-message__body chat-edit-focus"
      :style="bodyStyles"
    >

      <!-- Speaker identity: name sits above, avatar floats into the prose so
           the message text displaces around it (both AI and user authors). -->
      <ReasoningDisclosure
        ref="reasoningDisclosure"
        :live-reasoning="hasReasoning ? props.message.reasoning_content : null"
        :saved-reasoning="hasReasoning ? props.message.reasoning_snapshot : null"
        :reasoning-active="reasoningActive"
        :auto-scroll-cancelled="props.autoScrollCancelled"
        :working-label="!isUserAuthor && props.message.phase === 'thinking' ? props.thinkingLabel : undefined"
        :active-label="props.thinkingLabel || 'thinking…'"
      >
        <template #identity>
          <div class="prose-message__name">{{ characterName }}</div>
          <!-- Director note badge and shared create/edit surface. The anchor stays
               mounted while a new note is being composed even though there is no
               persisted badge to show yet. -->
          <div
            v-if="features.directorNotes !== false && isUserAuthor && (hasDirectorNote || isEditingDirectorNote) && isSettled && !isEditing"
            class="director-note-anchor"
            @mouseenter="handleDirectorNoteMouseEnter"
            @mouseleave="handleDirectorNoteMouseLeave"
          >
            <button
              v-if="hasDirectorNote"
              type="button"
              class="director-note-badge"
              :class="{ 'director-note-badge--expired': directorNoteStatus === 'expired' }"
              :title="directorNoteStatus === 'expired' ? 'Director note expired' : 'Director note active'"
              @click.stop="toggleDirectorNotePreview"
            >
              <MovieOpenIcon
                v-if="directorNoteStatus !== 'expired'"
                :size="16"
                class="director-note-badge__icon director-note-badge__icon--active"
                :decorative="true"
              />
              <MovieOpenOutlineIcon
                v-else
                :size="16"
                class="director-note-badge__icon director-note-badge__icon--expired"
                :decorative="true"
              />
            </button>

            <!-- Director note preview popover -->
            <div
              v-if="showDirectorNotePreview"
              ref="directorNotePreviewRef"
              class="director-note-preview"
              :class="{ 'director-note-preview--expired': directorNoteStatus === 'expired' }"
            >
              <template v-if="!isEditingDirectorNote">
                <div class="director-note-preview__meta">
                  <span>{{ directorNoteStatus === 'expired' ? 'Expired' : 'Active' }}</span>
                  <span>{{ directorNotePlacementLabel }}</span>
                  <span>{{ directorNoteVisibilityLabel }}</span>
                </div>
                <p class="director-note-preview__content">{{ directorNotePreviewText }}</p>
                <div class="director-note-preview__actions">
                  <BaseButton
                    v-if="canEditDirectorNote"
                    variant="secondary"
                    size="sm"
                    @click="startEditingDirectorNote"
                  >
                    Edit
                  </BaseButton>
                </div>
              </template>

              <template v-else>
                <div class="director-note-editor">
                  <CoreTextareaField
                    v-model="directorNoteEditForm.content"
                    name="director-note-edit"
                    :autogrow="true"
                    :input-class="'w-full'"
                    hide-details="never"
                    class="w-full"
                  />

                  <div class="director-note-editor__group">
                    <span class="director-note-editor__label">Placement</span>
                    <div class="director-note-editor__toggle">
                      <button
                        type="button"
                        class="director-note-editor__option"
                        :class="{ 'director-note-editor__option--active': directorNoteEditForm.placement === 'append' }"
                        @click="directorNoteEditForm.placement = 'append'"
                      >
                        Append
                      </button>
                      <button
                        type="button"
                        class="director-note-editor__option"
                        :class="{ 'director-note-editor__option--active': directorNoteEditForm.placement === 'prepend' }"
                        @click="directorNoteEditForm.placement = 'prepend'"
                      >
                        Prepend
                      </button>
                    </div>
                  </div>

                  <div class="director-note-editor__group">
                    <label class="director-note-editor__checkbox">
                      <input v-model="directorNoteEditUsesExpiry" type="checkbox" />
                      <span>Expires</span>
                    </label>
                  </div>

                  <div v-if="directorNoteEditUsesExpiry" class="director-note-editor__group">
                    <span class="director-note-editor__label">AI sees this message</span>
                    <input
                      v-model="directorNoteEditVisibilityInput"
                      class="director-note-editor__input"
                      type="number"
                      min="1"
                      placeholder="1"
                    />
                  </div>

                  <div class="director-note-preview__actions">
                    <BaseButton variant="primary" size="sm" @click="saveDirectorNoteEdit">Save</BaseButton>
                    <BaseButton variant="secondary" size="sm" @click="cancelDirectorNoteEdit">Cancel</BaseButton>
                    <BaseButton v-if="hasDirectorNote" variant="danger" size="sm" @click="deleteDirectorNote">Remove</BaseButton>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </template>
      </ReasoningDisclosure>

      <button
        type="button"
        v-if="features.portraits !== false"
        class="prose-message__avatar-btn"
        :aria-label="`Enlarge ${characterName} portrait`"
        @click="handleAvatarClick"
      >
        <BaseImage
          class="prose-message__avatar"
          :src="avatarSrc"
          :alt="characterName"
          :image-focus="avatarFocus"
          fallback-size="sm"
        />
      </button>

      <!-- Status indicators (sending / thinking / error) -->
      <div v-if="showStatusIndicator" class="prose-message__status">
        <span v-if="props.message.phase === 'thinking'" class="prose-message__status-text prose-message__status-text--muted animate-pulse">Thinking...</span>
        <div v-else-if="props.message.phase === 'error'" class="prose-message__status-text prose-message__status-text--error" v-html="renderedMessageContent" />
      </div>

      <!-- Streaming content -->
      <div
        v-else-if="props.message.phase === 'streaming'"
        class="prose-message__content prose-message__content--streaming story-prose"
        v-html="renderedMessageContent"
      />

      <!-- Normal content or edit mode -->
      <div v-else>
        <!-- View mode -->
        <div v-if="!isEditing" class="prose-message__content-shell">
          <!-- Main content -->
          <div
            ref="contentRef"
            class="prose-message__content story-prose"
            v-html="renderedContent"
          />
        </div>

        <!-- Edit mode: a contenteditable region shares the rendered message's
             exact typography and sits in the same flow slot, so the text wraps
             around the floated avatar identically to view mode. -->
        <div v-else class="prose-message__content-shell">
          <InlineProseEditor :content="props.message.content" :disabled="readOnly"
            content-class="prose-message__content prose-message__content--editing"
            @save="emit('edit', $event)" @saved="stopEditing" @cancel="stopEditing" />
        </div>
      </div>

      <slot name="status" />

      <!-- Hidden full-body sizer for alternatives -->
      <div
        v-if="showBodySizer"
        ref="bodySizerRef"
        class="prose-message__body-sizer"
        aria-hidden="true"
      >
        <div
          v-for="option in renderedOptions"
          :key="option.id"
          class="prose-message__body-measure"
        >
          <div class="prose-message__name-row">
            <div class="prose-message__name">{{ characterName }}</div>
          </div>

          <div
            v-if="reasoningPanelText"
            class="prose-reasoning-panel"
          >{{ reasoningPanelText }}</div>

          <div v-if="features.portraits !== false"
            class="prose-message__avatar-btn prose-message__avatar-btn--sizer" />

          <div class="prose-message__content-shell">
            <div
              class="prose-message__content story-prose"
              v-html="option.html"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Action row -->
    <div
      v-if="!actionsDisabled && (showActionRow || ($slots.actions && !isEditing))"
      class="prose-message__actions chat-message-actions"
      :style="actionRowStyles"
    >
      <BaseButton v-if="canResend" @click="emit('resend')" class="prose-action-btn chat-action-button" title="Resend message" variant="ghost" size="icon">
        <ResendIcon />
      </BaseButton>
      <BaseButton v-if="canEdit" @click="startEditing" class="prose-action-btn chat-action-button" title="Edit message" variant="ghost" size="icon">
        <EditIcon />
      </BaseButton>
      <BaseButton v-if="canAddDirectorNote" @click="startAddingDirectorNote" class="prose-action-btn chat-action-button" title="Add director note" variant="ghost" size="icon">
        <MovieOpenOutlineIcon :decorative="true" />
      </BaseButton>
      <BaseButton
        v-if="canFork"
        :disabled="forkDisabled"
        @click="emit('fork')"
        class="prose-action-btn chat-action-button"
        :title="forking ? 'Forking conversation' : 'Fork conversation here'"
        variant="ghost"
        size="icon"
      >
        <ForkIcon />
      </BaseButton>
      <BaseButton v-if="canReroll" @click="emit('reroll')" class="prose-action-btn chat-action-button prose-action-btn--reroll" title="Generate alternative response" variant="ghost" size="icon">
        <RerollIcon />
      </BaseButton>
      <BaseButton v-if="canDelete" @click="emit('delete')" class="prose-action-btn chat-action-button prose-action-btn--delete" title="Delete message and all following" variant="ghost" size="icon">
        <DeleteIcon />
      </BaseButton>
      <BaseButton
        v-if="showAlternativeNavigation"
        :disabled="!canGoToPreviousAlternative"
        @click="goToPreviousAlternative"
        class="prose-action-btn chat-action-button"
        title="Previous alternative"
        variant="ghost"
        size="icon"
      >
        <ArrowLeftIcon />
      </BaseButton>
      <BaseButton
        v-if="showAlternativeNavigation"
        :disabled="!canGoToNextAlternative"
        @click="goToNextAlternative"
        class="prose-action-btn chat-action-button"
        title="Next alternative"
        variant="ghost"
        size="icon"
      >
        <ArrowRightIcon />
      </BaseButton>
      <slot name="actions" />
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { parseMarkdown } from '@/utils/markdown'
import '@/components/chat/storyProse.css'
import { useConversationTypography } from '@/components/chat/useConversationTypography'
import {
  getDirectorNoteStatus,
  normalizeVisibilityCount,
  type DirectorNoteDraft,
} from '@/features/story-conversation/directorNotes'
import MovieOpenIcon from 'vue-material-design-icons/MovieOpen.vue'
import MovieOpenOutlineIcon from 'vue-material-design-icons/MovieOpenOutline.vue'
import ResendIcon from '@/components/chat/icons/ResendIcon.vue'
import EditIcon from '@/components/chat/icons/EditIcon.vue'
import InlineProseEditor from '@/components/chat/InlineProseEditor.vue'
import '@/components/chat/chatEditing.css'
import ForkIcon from '@/components/chat/icons/ForkIcon.vue'
import RerollIcon from '@/components/chat/icons/RerollIcon.vue'
import DeleteIcon from '@/components/chat/icons/DeleteIcon.vue'
import ArrowLeftIcon from '@/components/chat/icons/ArrowLeftIcon.vue'
import ArrowRightIcon from '@/components/chat/icons/ArrowRightIcon.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import BaseImage from '@/components/ui/BaseImage.vue'
import ReasoningDisclosure from '@/components/chat/ReasoningDisclosure.vue'
import CoreTextareaField from '@/components/core/form/fields/CoreTextareaField.vue'
import type { ChatCharacterIdentity, ChatPersonaIdentity, ChatMessageFeatures } from '@/components/chat/types'
import type { AvatarZoomPayload } from '@/components/chat/avatarZoom'
import type { MessagePhase, Variant } from '@/lib/messageSlots'

type DirectorNoteLike = {
  content?: string | null
  placement?: 'append' | 'prepend' | string | null
  visibility_count?: number | null
}

type ChatMessageLike = {
  id?: number | string | null
  content: string
  is_user_author?: boolean
  phase: MessagePhase
  director_note?: DirectorNoteLike | null
  // Ephemeral, in-memory only: the character's reasoning while/after it streams.
  reasoning_content?: string | null
  reasoning_snapshot?: string | null
}

type Props = {
  message: ChatMessageLike
  character: ChatCharacterIdentity
  persona: ChatPersonaIdentity
  timelineMessages?: ChatMessageLike[]
  variants?: Variant[]
  activeVariantId?: string | number | null
  isMostRecent?: boolean
  /** True when the user scrolled away while this message streamed. */
  autoScrollCancelled?: boolean
  thinkingLabel?: string
  features?: ChatMessageFeatures
  actionsDisabled?: boolean
  readOnly?: boolean
  forkDisabled?: boolean
  forking?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  timelineMessages: () => [],
  variants: () => [],
  activeVariantId: null,
  isMostRecent: false,
  features: () => ({}),
  actionsDisabled: false,
  readOnly: false,
  forkDisabled: false,
  forking: false,
})

const emit = defineEmits<{
  (event: 'edit', content: string): void
  (event: 'fork'): void
  (event: 'reroll'): void
  (event: 'delete'): void
  (event: 'resend'): void
  (event: 'director-note:update', payload: DirectorNoteDraft): void
  (event: 'director-note:delete'): void
  (event: 'update:activeVariant', value: string | number): void
  (event: 'avatar-zoom', payload: AvatarZoomPayload): void
}>()

// Portrait zoom: emit the clicked avatar's data plus its element so the
// singleton overlay in ConversationView can FLIP-animate from this exact rect.
const handleAvatarClick = (event: MouseEvent): void => {
  emit('avatar-zoom', {
    src: avatarSrc.value,
    alt: characterName.value,
    focus: avatarFocus.value,
    el: event.currentTarget as HTMLElement,
  })
}

// --- Editing state ---
// These refs are read by computed values and immediate watchers below, so they
// must be initialized before that reactive graph is created.
const isEditing = ref(false)
const isEditingDirectorNote = ref(false)
const messageEl = ref<HTMLElement | null>(null)

// --- Computed ---
const isUserAuthor = computed(() => Boolean(props.message.is_user_author))
// Reasoning is only ever the AI character's. It shows above the reply as a quiet
// disclosure; while it streams it also stands in for the generic "Thinking..." text.
const hasReasoning = computed(() =>
  props.features.reasoning !== false
  && !isUserAuthor.value
  && Boolean(props.message.reasoning_content?.trim() || props.message.reasoning_snapshot?.trim()),
)
const contentStarted = computed(() => Boolean(props.message.content?.trim()))

const reasoningDisclosure = ref<InstanceType<typeof ReasoningDisclosure> | null>(null)
const reasoningPanelText = computed(() => reasoningDisclosure.value?.panelText ?? '')
const reasoningActive = computed(() =>
  (props.message.phase === 'thinking' || props.message.phase === 'streaming') && !contentStarted.value,
)

const showStatusIndicator = computed(() => {
  const phase = props.message.phase
  if (phase === 'complete' || phase === 'streaming') return false
  // The reasoning panel already conveys the "thinking" state; don't double it up.
  if (phase === 'thinking' && (hasReasoning.value || props.thinkingLabel)) return false
  return true
})
const isSettled = computed(() => props.message.phase === 'complete')
const avatarSrc = computed(() => (isUserAuthor.value ? props.persona.imgpath : props.character.imgpath) || '')
const avatarFocus = computed(() => (isUserAuthor.value ? props.persona.img_focus : props.character.img_focus) || null)
const characterName = computed(() => (isUserAuthor.value ? props.persona?.persona_name : props.character?.character_name) || '')
const hasRealId = computed(() => {
  const id = props.message?.id
  if (id === null || id === undefined) return false
  return !String(id).startsWith('temp-')
})
const hasDirectorNote = computed(() => Boolean(props.message?.director_note?.content?.trim?.()))

// --- Variants ---
const allOptions = computed<Variant[]>(() => props.variants ?? [])
const hasAlternatives = computed(() => allOptions.value.length > 1)

const activeAlternativeIndex = computed(() => {
  const idx = allOptions.value.findIndex(option => String(option.id) === String(props.activeVariantId))
  return idx === -1 ? 0 : idx
})

const activeContent = computed(() => allOptions.value[activeAlternativeIndex.value]?.content ?? props.message.content)

// Cache by displayed text rather than by render: unrelated timeline updates
// must not reparse or resanitize completed prose.
const renderedContent = computed(() => parseMarkdown(activeContent.value, {}, { dialogue: true }))
const renderedMessageContent = computed(() => parseMarkdown(props.message.content, {}, { dialogue: true }))
const renderedOptions = computed(() => allOptions.value.map((option) => ({ id: option.id, html: parseMarkdown(option.content, {}, { dialogue: true }) })))

// --- Alternative body sizing ---
const showBodySizer = computed(() => hasAlternatives.value && isSettled.value && !isEditing.value)
const bodyMinHeight = ref(0)
const bodySizerRef = ref<HTMLElement | null>(null)
let measurementRafId: number | null = null
const contentRef = ref<HTMLElement | null>(null)
const currentContentWidth = ref(0)
const baseActionMarginPx = 8

const actionRowStyles = computed(() => ({ marginTop: `${baseActionMarginPx}px` }))

const bodyStyles = computed(() => {
  if (!showBodySizer.value || bodyMinHeight.value <= 0) return {}
  return { minHeight: `${bodyMinHeight.value}px` }
})

const measureContentHeights = () => {
  if (!showBodySizer.value) {
    bodyMinHeight.value = 0
    return
  }
  const container = bodySizerRef.value
  if (!container) return
  let maxHeight = 0
  Array.from(container.children).forEach((child) => {
    const h = (child as HTMLElement).getBoundingClientRect().height
    if (h > maxHeight) maxHeight = h
  })
  bodyMinHeight.value = maxHeight
  const contentEl = contentRef.value
  currentContentWidth.value = contentEl ? contentEl.offsetWidth : 0
}

const scheduleMeasurement = () => {
  if (!showBodySizer.value) {
    bodyMinHeight.value = 0
    return
  }
  if (typeof window === 'undefined') { measureContentHeights(); return }
  if (measurementRafId !== null) { window.cancelAnimationFrame(measurementRafId); measurementRafId = null }
  measurementRafId = window.requestAnimationFrame(() => { measurementRafId = null; measureContentHeights() })
}

const typographyKey = useConversationTypography()
watch([allOptions, activeAlternativeIndex, showBodySizer, typographyKey], () => {
  if (!showBodySizer.value) {
    bodyMinHeight.value = 0
    currentContentWidth.value = 0
    return
  }
  scheduleMeasurement()
}, { immediate: true })

const handleResize = () => scheduleMeasurement()

onMounted(() => {
  scheduleMeasurement()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
    document.addEventListener('pointerdown', handlePointerDownOutsidePreview)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
    document.removeEventListener('pointerdown', handlePointerDownOutsidePreview)
    if (measurementRafId !== null) { window.cancelAnimationFrame(measurementRafId); measurementRafId = null }
  }
})

// --- Permissions ---
const canEdit = computed(() => !props.readOnly && props.features.edit !== false && isSettled.value)
const canAddDirectorNote = computed(() => props.features.directorNotes !== false && !props.readOnly
  && isUserAuthor.value
  && isSettled.value
  && hasRealId.value
  && !hasDirectorNote.value
  && !isEditingDirectorNote.value)
const canFork = computed(() => props.features.fork !== false && !props.readOnly && isSettled.value && hasRealId.value)
const canReroll = computed(() => props.features.reroll !== false && !props.readOnly && !isUserAuthor.value && props.isMostRecent && isSettled.value && hasRealId.value)
const canDelete = computed(() => props.features.delete !== false && !props.readOnly && isSettled.value)
const canResend = computed(() => props.features.resend !== false && !props.readOnly && isUserAuthor.value && props.isMostRecent && isSettled.value)
const showAlternativeNavigation = computed(() => props.features.alternatives !== false && allOptions.value.length > 1 && isSettled.value)
const canGoToPreviousAlternative = computed(() => !props.readOnly && activeAlternativeIndex.value > 0)
const canGoToNextAlternative = computed(() => !props.readOnly && activeAlternativeIndex.value < allOptions.value.length - 1)

const showActionRow = computed(() => {
  return (canEdit.value || canAddDirectorNote.value || canFork.value || canReroll.value || canDelete.value || showAlternativeNavigation.value || canResend.value) && !isEditing.value
})

// --- Editing ---
const stopEditing = () => { isEditing.value = false }
const startEditing = () => { isEditing.value = true }

// --- Director notes ---
const showDirectorNotePreview = ref(false)
const directorNotePinned = ref(false)
const directorNotePreviewRef = ref<HTMLElement | null>(null)
const directorNoteEditForm = ref<DirectorNoteDraft>({ content: '', placement: 'append', visibility_count: null })

const directorNoteStatus = computed(() => getDirectorNoteStatus(
  { message: props.message, messages: props.timelineMessages || [] } as unknown as Parameters<typeof getDirectorNoteStatus>[0],
))
const directorNotePlacementLabel = computed(() => props.message?.director_note?.placement === 'prepend' ? 'Prepend' : 'Append')
const directorNoteVisibilityLabel = computed(() => {
  const count = props.message?.director_note?.visibility_count
  return count == null ? 'No expiry' : `${count} turn${count === 1 ? '' : 's'}`
})
const directorNotePreviewText = computed(() => {
  const content = props.message?.director_note?.content?.trim?.() || ''
  return content.length <= 160 ? content : `${content.slice(0, 157)}...`
})
const canEditDirectorNote = computed(() => !props.readOnly && isUserAuthor.value && hasDirectorNote.value && isSettled.value)

const directorNoteEditUsesExpiry = computed({
  get: () => directorNoteEditForm.value.visibility_count !== null,
  set: (next: boolean) => {
    directorNoteEditForm.value.visibility_count = next
      ? normalizeVisibilityCount(directorNoteEditForm.value.visibility_count) ?? 1
      : null
  },
})

const directorNoteEditVisibilityInput = computed({
  get: () => directorNoteEditForm.value.visibility_count === null ? '' : String(directorNoteEditForm.value.visibility_count),
  set: (value: string) => { directorNoteEditForm.value.visibility_count = normalizeVisibilityCount(value) ?? 1 },
})

function syncDirectorNoteEditForm() {
  directorNoteEditForm.value = {
    content: props.message?.director_note?.content || '',
    placement: props.message?.director_note?.placement === 'prepend' ? 'prepend' : 'append',
    visibility_count: props.message?.director_note?.visibility_count == null
      ? null
      : normalizeVisibilityCount(props.message?.director_note?.visibility_count) ?? 1,
  }
}

watch(() => props.message?.director_note, () => {
  if (!props.message?.director_note?.content?.trim?.()) {
    showDirectorNotePreview.value = false
    directorNotePinned.value = false
    isEditingDirectorNote.value = false
    return
  }
  syncDirectorNoteEditForm()
}, { immediate: true, deep: true })

const openDirectorNotePreview = (pin = false) => {
  if (!hasDirectorNote.value) return
  showDirectorNotePreview.value = true
  if (pin) directorNotePinned.value = true
}
const closeDirectorNotePreview = () => {
  if (isEditingDirectorNote.value) return
  showDirectorNotePreview.value = false
  directorNotePinned.value = false
}
const handleDirectorNoteMouseEnter = () => {
  if (typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches) openDirectorNotePreview(false)
}
const handleDirectorNoteMouseLeave = () => {
  if (!directorNotePinned.value) closeDirectorNotePreview()
}
const toggleDirectorNotePreview = () => {
  if (!showDirectorNotePreview.value || !directorNotePinned.value) {
    syncDirectorNoteEditForm()
    showDirectorNotePreview.value = true
    directorNotePinned.value = true
    return
  }
  isEditingDirectorNote.value = false
  closeDirectorNotePreview()
}
const startEditingDirectorNote = () => {
  syncDirectorNoteEditForm()
  isEditingDirectorNote.value = true
  showDirectorNotePreview.value = true
  directorNotePinned.value = true
}
const startAddingDirectorNote = () => {
  directorNoteEditForm.value = { content: '', placement: 'append', visibility_count: null }
  isEditingDirectorNote.value = true
  showDirectorNotePreview.value = true
  directorNotePinned.value = true
}
const cancelDirectorNoteEdit = () => {
  isEditingDirectorNote.value = false
  if (!hasDirectorNote.value) {
    showDirectorNotePreview.value = false
    directorNotePinned.value = false
    directorNoteEditForm.value = { content: '', placement: 'append', visibility_count: null }
    return
  }
  syncDirectorNoteEditForm()
}
const saveDirectorNoteEdit = () => {
  const content = directorNoteEditForm.value.content?.trim?.() || ''
  if (!content) {
    if (hasDirectorNote.value) deleteDirectorNote()
    else cancelDirectorNoteEdit()
    return
  }
  emit('director-note:update', {
    content,
    placement: directorNoteEditForm.value.placement === 'prepend' ? 'prepend' : 'append',
    visibility_count: directorNoteEditUsesExpiry.value
      ? normalizeVisibilityCount(directorNoteEditForm.value.visibility_count) ?? 1
      : null,
  })
  isEditingDirectorNote.value = false
  if (!hasDirectorNote.value) {
    showDirectorNotePreview.value = false
    directorNotePinned.value = false
  }
}
const deleteDirectorNote = () => {
  if (!hasDirectorNote.value) {
    cancelDirectorNoteEdit()
    return
  }
  emit('director-note:delete')
  isEditingDirectorNote.value = false
  showDirectorNotePreview.value = false
  directorNotePinned.value = false
}
const handlePointerDownOutsidePreview = (event: PointerEvent) => {
  const target = event.target as HTMLElement | null
  if (!showDirectorNotePreview.value || !directorNotePinned.value) return
  if (target && directorNotePreviewRef.value?.contains(target)) return
  if (target?.closest?.('.director-note-badge')) return
  closeDirectorNotePreview()
}

// --- Alternative navigation ---
const goToPreviousAlternative = () => {
  if (activeAlternativeIndex.value > 0) {
    const newIndex = activeAlternativeIndex.value - 1
    const option = allOptions.value[newIndex]
    if (!option) return
    emit('update:activeVariant', option.id)
  }
}
const goToNextAlternative = () => {
  if (activeAlternativeIndex.value < allOptions.value.length - 1) {
    const newIndex = activeAlternativeIndex.value + 1
    const option = allOptions.value[newIndex]
    if (!option) return
    emit('update:activeVariant', option.id)
  }
}
</script>

<style scoped>
@reference '@/assets/main.css';

.prose-message {
  position: relative;
  width: 100%;
  padding: 4px 0;
  list-style: none;
}

/* Speaker identity: name above, portrait floated into the prose. The float,
   size and downward nudge live on the button so the clickable target is the
   portrait square itself and the message text wraps around it as before. */
.prose-message__avatar-btn {
  float: left;
  display: block;
  width: 84px;
  height: 84px;
  margin: 0 14px 6px 0;
  padding: 0;
  border: 0;
  background: none;
  border-radius: 10px;
  cursor: zoom-in;
  /* Nudge the portrait down visually without shifting the text wrap. */
  position: relative;
  top: 6px;
  -webkit-tap-highlight-color: transparent;
}

.prose-message__avatar-btn:focus-visible {
  outline: 2px solid var(--theme-primary);
  outline-offset: 2px;
}

.prose-message__avatar {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  object-fit: cover;
  display: block;
}

.prose-message__name-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  margin-bottom: 4px;
}

.prose-message__name {
  min-width: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--theme-on-surface);
  letter-spacing: 0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mirror the visible reasoning panel in the hidden alternative-height sizer. */
.prose-reasoning-panel {
  max-height: 160px;
  overflow-y: auto;
  margin: 0 0 0.5rem;
  padding: 0.1rem 0 0.1rem 0.7rem;
  border-left: 2px solid color-mix(in srgb, var(--theme-primary) 22%, var(--theme-border));
  color: color-mix(in srgb, var(--theme-on-surface) 55%, transparent);
  font-size: 0.9rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Body — flow-root establishes a BFC so the floated avatar is contained and
   the message prose wraps around it instead of overflowing the action row. */
.prose-message__body {
  position: relative;
  display: flow-root;
}

/* Content */
.prose-message__content {
  @apply text-prose-lg text-on-surface break-words;
  color: var(--story-text-color, var(--theme-on-surface));
  font-family: var(--story-font-family, Georgia, serif);
  line-height: 1.7;
}

.prose-message__content--streaming {
  opacity: 0.8;
}

.prose-message__content :deep(p) {
  @apply m-0 mb-2.5 whitespace-pre-wrap;
}

.prose-message__content :deep(br) {
  content: "" !important;
  display: block !important;
  margin-bottom: 10px !important;
}

/* Status */
.prose-message__status {
  @apply px-3 py-2 rounded-ui;
}

.prose-message__status-text {
  font-size: 0.95rem;
}

.prose-message__status-text--muted {
  @apply text-on-surface opacity-70 italic;
}

.prose-message__status-text--error {
  @apply text-danger;
}

.status-thinking .prose-message__body {
  opacity: 0.8;
}

/* Director note */
.director-note-anchor {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  z-index: 2;
}

.director-note-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.director-note-badge--expired { opacity: 0.55; }

.director-note-badge__icon { display: block; }
.director-note-badge__icon--active { @apply text-primary; }
.director-note-badge__icon--expired { @apply text-secondary; }

.director-note-preview {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: min(280px, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
  border: 1px solid var(--theme-border-hover);
  border-radius: 10px;
  background: var(--theme-surface-solid);
  padding: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.24);
  overflow-wrap: anywhere;
  z-index: 12;
}

.director-note-preview--expired { opacity: 0.92; }

.director-note-preview__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 0.8rem;
  color: var(--theme-text-secondary);
}

.director-note-preview__content {
  margin: 0;
  white-space: pre-wrap;
  font-size: 0.9rem;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.director-note-preview__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}

.director-note-editor {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.director-note-editor__group { display: flex; flex-direction: column; gap: 6px; }
.director-note-editor__label { font-size: 0.82rem; color: var(--theme-text-secondary); }
.director-note-editor__toggle {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 1px solid var(--theme-border-hover);
  border-radius: 10px;
  padding: 2px;
  background: var(--theme-shimmer-from);
}
.director-note-editor__option {
  border: 0;
  background: transparent;
  color: var(--theme-text-secondary);
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
}
.director-note-editor__option--active {
  background: var(--theme-surface-interactive);
  color: var(--theme-on-surface);
}
.director-note-editor__checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--theme-on-surface);
}
.director-note-editor__input {
  width: 100%;
  border: 1px solid var(--theme-border-hover);
  border-radius: 10px;
  background: var(--theme-shimmer-from);
  color: var(--theme-on-surface);
  padding: 9px 11px;
}

/* Hidden body sizer */
.prose-message__body-sizer {
  /* Keep every alternative measurable without letting their stacked boxes
     enlarge the conversation's scrollable overflow. */
  height: 0;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  visibility: hidden;
  pointer-events: none;
  z-index: -1;
  width: 100%;
}

.prose-message__body-measure {
  position: relative;
  display: flow-root;
  width: 100%;
}

/* Action row */
.prose-action-btn--reroll svg { @apply text-success; }
.prose-action-btn--delete svg { @apply text-danger; }

/* Mobile touch targets */
@media (max-width: 768px) {
  .prose-message__avatar-btn {
    width: 68px;
    height: 68px;
    margin-right: 12px;
  }

  .prose-message__content {
    font-size: 1.0625rem;
    line-height: 1.65;
  }
}
</style>
